import { PerspectiveCamera, Vector3 } from "three";
import { OrbitControlsWithHover } from "./orbitControlsWithHover";
import { easeInOutQuart } from "../common";
import { EPSILON, MAX_GEOMETRY_DIMENSION, TWO_PI } from "../config";
const VERTICAL_FOV = 40;
const NEAR = 5;
const FAR = 3000;
const defaultOptions = {
  panning: false,
  hover: false,
  invertXAxis: false,
  invertYAxis: false,
  minZoom: 1,
  maxZoom: 1,
  containerPadding: 30
};
export class OrbitalCamera {
  constructor(cameraOptions, _ref) {
    let {
      aspectRatio,
      domElement
    } = _ref;
    var _a, _b, _c, _d, _e;
    Object.defineProperty(this, "camera", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "controls", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "initialZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "containerPadding", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "lastPosition", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "initialOrbitRadius", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "_requiresUpdate", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "focalPointAnimation", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "rotationAnimation", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "zoomAnimation", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "focalPointResolver", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "lookAtPromiseResolver", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "zoomPromiseResolver", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    const {
      panning,
      hover,
      invertXAxis,
      invertYAxis,
      minZoom,
      maxZoom,
      containerPadding
    } = Object.assign(Object.assign({}, defaultOptions), cameraOptions);
    // Initialize and orient the camera
    this.camera = new PerspectiveCamera(VERTICAL_FOV, aspectRatio, NEAR, FAR);
    const initialOrientation = (_a = cameraOptions.initialOrientation) !== null && _a !== void 0 ? _a : {
      azimuthAngle: 0,
      polarAngle: Math.PI / 2
    };
    this.controls = new OrbitControlsWithHover(this.camera, domElement, {
      vertical: VERTICAL_FOV
    }, {
      invertXAxis,
      invertYAxis
    }, cameraOptions.orbitOptions);
    this.controls.setRotation(initialOrientation.azimuthAngle, initialOrientation.polarAngle);
    if (cameraOptions.initialFocalPoint) {
      const focalPoint = cameraOptions.initialFocalPoint;
      this.controls.setTarget(new Vector3(focalPoint.x, focalPoint.y, focalPoint.z));
    }
    this.controls.rotateSpeed = (_b = cameraOptions.rotationSpeed) !== null && _b !== void 0 ? _b : 0.07;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.1;
    // Hovering should effect both the control hover (attaches mouse bindings) and
    // rotation which prevents the camera from moving if bindings are set
    this.controls.enableHover = hover;
    this.controls.enableRotate = hover;
    this.controls.enablePan = panning;
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
    this.initialZoom = (_c = cameraOptions.initialZoom) !== null && _c !== void 0 ? _c : 1.0;
    this.controls.scrollZoomSpeed = (_d = cameraOptions.scrollZoomSpeed) !== null && _d !== void 0 ? _d : 1.0;
    this.controls.pinchZoomSpeed = (_e = cameraOptions.pinchZoomSpeed) !== null && _e !== void 0 ? _e : 1.0;
    this.containerPadding = containerPadding;
    // Disable zooming when min and max zoom are the same
    this.controls.enableZoom = this.minZoom !== this.maxZoom;
    this.lastPosition = new Vector3();
    this._requiresUpdate = true;
    // Rotation animation defaults
    this.rotationAnimation = {
      animating: false,
      duration: 2,
      elapsedTime: 0,
      target: {
        azimuthAngle: 0,
        polarAngle: 0
      },
      initial: {
        azimuthAngle: 0,
        polarAngle: 0
      },
      easingFunction: easeInOutQuart
    };
    // Focal point animation defaults
    this.focalPointAnimation = {
      animating: false,
      elapsedTime: 0,
      duration: 2,
      initialPoint: new Vector3(),
      targetPoint: new Vector3(),
      easingFunction: easeInOutQuart
    };
    // Zoom animation defaults
    this.zoomAnimation = {
      animating: false,
      elapsedTime: 0,
      duration: 0.5,
      targetZoom: 0,
      initialZoom: 0,
      easingFunction: easeInOutQuart
    };
    this.initialOrbitRadius = MAX_GEOMETRY_DIMENSION;
  }
  get requiresUpdate() {
    return this._requiresUpdate;
  }
  get zoomFactor() {
    return this.initialOrbitRadius / this.controls.getOrbitRadius();
  }
  initializeCamera(geometrySize) {
    const radius = this.calculateOrbitRadius(geometrySize);
    // Set the camera to the desired initial radius
    this.controls.setRadius(radius * this.initialZoom);
    this.controls.setRadiusLimits(this.minZoom * radius, this.maxZoom * radius);
    // Update the radius and zoom limits
    this.initialOrbitRadius = radius;
    // Update the pan boundaries based on geometry size
    const padding = this.containerPadding * 0.2;
    this.controls.setPanBounds(new Vector3(geometrySize.width * -0.5 - padding, geometrySize.height * -0.5 - padding, geometrySize.depth * -0.5 - padding), new Vector3(geometrySize.width * 0.5 + padding, geometrySize.height * 0.5 + padding, geometrySize.depth * 0.5 + padding));
  }
  resizeCamera(geometrySize) {
    const radius = this.calculateOrbitRadius(geometrySize);
    const previousZoomFactor = this.zoomFactor;
    // Persevere the zoom and update the radius limits
    this.controls.setRadius(radius / previousZoomFactor);
    this.controls.setRadiusLimits(this.minZoom * radius, this.maxZoom * radius);
    this.initialOrbitRadius = radius;
  }
  computeZoom(geometrySize) {
    const radius = this.calculateOrbitRadius(geometrySize);
    return radius / this.initialOrbitRadius;
  }
  // zoom value is restricted by defined max and min zooms in update function
  adjustZoomFactor(zoom) {
    let duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.5;
    let easingFunction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : easeInOutQuart;
    // zoom factor can only be greater than zero and be between the specified min and max values
    if (zoom > 0 && zoom >= this.minZoom && zoom <= this.maxZoom) {
      // If there was an existing task, resolve it and move on
      if (this.zoomPromiseResolver) {
        this.zoomPromiseResolver();
      }
      this.zoomAnimation.elapsedTime = 0;
      this.zoomAnimation.duration = duration;
      this.zoomAnimation.initialZoom = this.controls.getOrbitRadius() / this.initialOrbitRadius;
      this.zoomAnimation.targetZoom = zoom;
      this.zoomAnimation.animating = true;
      this.zoomAnimation.easingFunction = easingFunction;
      return new Promise(resolve => {
        this.zoomPromiseResolver = resolve;
      });
    }
    return Promise.resolve();
  }
  set aspectRatio(newAspectRatio) {
    // Store the initial azimuth and polar angles
    const azimuth = this.controls.getAzimuthalAngle();
    const polar = this.controls.getPolarAngle();
    const radius = this.controls.getOrbitRadius();
    // Update the camera projection, this will reset azimuth and polar angles
    this.camera.aspect = newAspectRatio;
    this.camera.updateProjectionMatrix();
    this.controls.setRotation(azimuth, polar);
    this.controls.setRadius(radius);
    this.controls.setRadiusLimits(radius * this.minZoom, radius * this.maxZoom);
  }
  update(deltaTime) {
    if (this.focalPointAnimation.animating) {
      const animation = this.focalPointAnimation;
      // Interpolate each dimension
      const focalX = animation.easingFunction(animation.elapsedTime, animation.initialPoint.x, animation.targetPoint.x - animation.initialPoint.x, animation.duration);
      const focalY = animation.easingFunction(animation.elapsedTime, animation.initialPoint.y, animation.targetPoint.y - animation.initialPoint.y, animation.duration);
      const focalZ = animation.easingFunction(animation.elapsedTime, animation.initialPoint.z, animation.targetPoint.z - animation.initialPoint.z, animation.duration);
      this.controls.setTarget(new Vector3(focalX, focalY, focalZ));
      animation.elapsedTime += deltaTime;
      // If the animation is finished ensure the camera is set to the right position
      if (animation.elapsedTime > animation.duration) {
        animation.animating = false;
        this.controls.setTarget(animation.targetPoint);
        this.focalPointResolver && this.focalPointResolver();
        this.focalPointResolver = undefined;
      }
    }
    if (this.rotationAnimation.animating) {
      let targetAzimuthAngle = this.controls.getAzimuthalAngle();
      let targetPolarAngle = this.controls.getPolarAngle();
      const animation = this.rotationAnimation;
      // Interpolate the azimuth and polar angles
      targetAzimuthAngle = this.rotationAnimation.easingFunction(animation.elapsedTime, animation.initial.azimuthAngle, animation.target.azimuthAngle - animation.initial.azimuthAngle, animation.duration);
      targetPolarAngle = this.rotationAnimation.easingFunction(animation.elapsedTime, animation.initial.polarAngle, animation.target.polarAngle - animation.initial.polarAngle, animation.duration);
      animation.elapsedTime += deltaTime;
      // If the animation is finished ensure the camera is set to the right position
      if (animation.elapsedTime > animation.duration) {
        targetAzimuthAngle = animation.target.azimuthAngle;
        targetPolarAngle = animation.target.polarAngle;
        animation.animating = false;
        this.lookAtPromiseResolver && this.lookAtPromiseResolver();
        this.lookAtPromiseResolver = undefined;
      }
      this.controls.setRotation(targetAzimuthAngle, targetPolarAngle);
    }
    if (this.zoomAnimation.animating) {
      const animation = this.zoomAnimation;
      let targetZoom = animation.easingFunction(animation.elapsedTime, animation.initialZoom, animation.targetZoom - animation.initialZoom, animation.duration);
      animation.elapsedTime += deltaTime;
      if (animation.elapsedTime > animation.duration) {
        targetZoom = animation.targetZoom;
        animation.animating = false;
        this.zoomPromiseResolver && this.zoomPromiseResolver();
        this.zoomPromiseResolver = undefined;
      }
      this.controls.setRadius(this.initialOrbitRadius * targetZoom);
    }
    this.controls.update();
    // Require a render update if the camera position changed since last frame
    this._requiresUpdate = this.lastPosition.distanceTo(this.camera.position) > EPSILON;
    this.lastPosition = this.camera.position.clone();
  }
  lookAt(target) {
    let duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
    let easingFunction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : easeInOutQuart;
    // If there was an existing task, resolve it and move on
    if (this.lookAtPromiseResolver) {
      this.lookAtPromiseResolver();
    }
    // Normalize both the initial and target orientations between 0 and 2pi
    const initialOrientation = this.normalizeOrientation({
      azimuthAngle: this.controls.getAzimuthalAngle(),
      polarAngle: this.controls.getPolarAngle()
    });
    const targetOrientation = this.normalizeOrientation(target);
    const azimuthLength = Math.abs(targetOrientation.azimuthAngle - initialOrientation.azimuthAngle);
    const polarLength = Math.abs(targetOrientation.polarAngle - initialOrientation.polarAngle);
    // Immediately return if the camera isn't changing significantly
    if (azimuthLength < EPSILON && polarLength < EPSILON) {
      return Promise.resolve();
    }
    // Ensure that azimuth rotations are less than PI (camera doesn't spin awkwardly)
    if (azimuthLength > Math.PI) {
      if (targetOrientation.azimuthAngle > initialOrientation.azimuthAngle) {
        initialOrientation.azimuthAngle += TWO_PI;
      } else {
        targetOrientation.azimuthAngle += TWO_PI;
      }
    }
    // Ensure that polar rotations are less than PI (camera doesn't spin awkwardly)
    if (polarLength > Math.PI) {
      if (targetOrientation.polarAngle > initialOrientation.polarAngle) {
        initialOrientation.polarAngle += TWO_PI;
      } else {
        targetOrientation.polarAngle += TWO_PI;
      }
    }
    this.rotationAnimation.elapsedTime = 0;
    this.rotationAnimation.duration = duration;
    this.rotationAnimation.initial = initialOrientation;
    this.rotationAnimation.target = targetOrientation;
    this.rotationAnimation.animating = true;
    this.rotationAnimation.easingFunction = easingFunction;
    return new Promise(resolve => {
      this.lookAtPromiseResolver = resolve;
    });
  }
  focusOn(target) {
    let duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
    let easingFunction = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : easeInOutQuart;
    // If there was an existing task, resolve it and move on
    if (this.focalPointResolver) {
      this.focalPointResolver();
    }
    this.focalPointAnimation.duration = duration;
    this.focalPointAnimation.initialPoint = this.controls.target;
    this.focalPointAnimation.targetPoint = target;
    this.focalPointAnimation.easingFunction = easingFunction;
    // Enable the animation
    this.focalPointAnimation.animating = true;
    this.focalPointAnimation.elapsedTime = 0;
    return new Promise(resolve => {
      this.focalPointResolver = resolve;
    });
  }
  dispose() {
    if (this.focalPointResolver) {
      this.focalPointResolver();
    }
    if (this.lookAtPromiseResolver) {
      this.lookAtPromiseResolver();
    }
    if (this.zoomPromiseResolver) {
      this.zoomPromiseResolver();
    }
    this.controls.dispose();
  }
  calculateOrbitRadius(geometrySize) {
    const verticalFov = VERTICAL_FOV * (Math.PI / 180);
    const horizontalFov = 2 * Math.atan(this.camera.aspect * Math.tan(verticalFov / 2));
    const verticalDistance = (geometrySize.height / 2 + this.containerPadding) / Math.tan(verticalFov / 2);
    const horizontalDistance = (geometrySize.width / 2 + this.containerPadding) / Math.tan(horizontalFov / 2);
    const depthDistance = (geometrySize.depth / 2 + this.containerPadding) / Math.tan(verticalFov / 2);
    return Math.max(verticalDistance, horizontalDistance, depthDistance);
  }
  get threeJsCamera() {
    return this.camera;
  }
  /**
   * @param  {CameraOrientation} input
   * @returns CameraOrientation
   * TODO: This should use the this.camera or be converted in a util
   */
  // eslint-disable-next-line class-methods-use-this
  normalizeOrientation(input) {
    let outputAzimuthAngle = input.azimuthAngle % TWO_PI;
    let outputPolarAngle = input.polarAngle % TWO_PI;
    if (outputAzimuthAngle < 0) {
      outputAzimuthAngle += TWO_PI;
    }
    if (outputPolarAngle < 0) {
      outputPolarAngle += TWO_PI;
    }
    return {
      azimuthAngle: outputAzimuthAngle,
      polarAngle: outputPolarAngle
    };
  }
}